import { useDispatch, useSelector } from 'react-redux';
import { ActionPlan } from '../types';
import { useEffect, useRef } from "react";
import {getDeletedActionPlanIds, getPlan, resetPlanFromServer} from "@/areas/culture-hub/slices/cultureHubInterventionsSlice";

const parseMaybeJsonObject = (value: unknown): any | null => {
  if (!value) return null;
  if (typeof value === 'object') return value as any;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const parseMaybeJsonArray = (value: unknown): any[] => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const firstNonEmptyArray = (...values: unknown[]): any[] | undefined => {
  for (const value of values) {
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }
  for (const value of values) {
    if (Array.isArray(value)) {
      return value;
    }
  }
  return undefined;
};

const extractTemplateTasks = (template: any): any[] => {
  if (!template || typeof template !== 'object') return [];

  if (Array.isArray(template.tasks)) {
    return template.tasks.filter((t: any) => t && typeof t === 'object');
  }

  if (Array.isArray(template.activities)) {
    return template.activities.flatMap((activity: any) =>
      Array.isArray(activity?.tasks)
        ? activity.tasks.filter((t: any) => t && typeof t === 'object')
        : []
    );
  }

  if (Array.isArray(template.phases)) {
    return template.phases.flatMap((phase: any) =>
      Array.isArray(phase?.activities)
        ? phase.activities.flatMap((activity: any) =>
            Array.isArray(activity?.tasks)
              ? activity.tasks.filter((t: any) => t && typeof t === 'object')
              : []
          )
        : []
    );
  }

  return [];
};

/**
 * Syncs server action plans to Redux interventions state.
 * This replaces the logic from useActionPlanState.ts
 */
export const useInterventionsPlanSync = (
  existingActionPlans: ActionPlan[] | undefined,
  assessmentPeriodId?: number | null,
  teamId?: number | null,
) => {
  const dispatch = useDispatch();
  const loadedIdsRef = useRef<string>('');
  const lastPeriodRef = useRef<number | null>(null);
  const lastTeamRef = useRef<number | null>(null);
  const forceSyncRef = useRef<boolean>(false);
  const plan = useSelector(getPlan);
  const deletedActionPlanIds = useSelector(getDeletedActionPlanIds);

  // When the selected assessment period or team changes, clear fingerprint and mark force sync
  useEffect(() => {
    loadedIdsRef.current = '';
    lastPeriodRef.current = null;
    lastTeamRef.current = null;
    forceSyncRef.current = true;
  }, [assessmentPeriodId, teamId]);

  useEffect(() => {
    // Undefined means query data is not ready yet. Do not clear local plan in this state.
    if (typeof existingActionPlans === 'undefined') {
      return;
    }

    const plans = existingActionPlans;
    const filteredPlans = teamId == null ? plans : plans.filter((ap) => ap.team_id === teamId);

    // Create a fingerprint of current action plans (include content so updates to tasks trigger a refresh)
    const currentIds = plans
      .map(ap => `${ap.id}:${JSON.stringify(ap.recommendation_json ?? {})}`)
      .sort()
      .join('|');

    // Only skip update if nothing changed and the period/team haven't changed unless a force sync is requested
    if (!forceSyncRef.current && currentIds === loadedIdsRef.current && lastPeriodRef.current === assessmentPeriodId && lastTeamRef.current === teamId) {
      return;
    }



    const savedPlans = filteredPlans
      .filter((ap) => !deletedActionPlanIds.includes(ap.id))
      .filter((ap) => !!ap.recommendation_json)
      .flatMap((ap) => {
        const json = ap.recommendation_json as any;
        if (!json || typeof json !== 'object') return [];

        const tasks = Array.isArray(json.tasks)
          ? json.tasks.filter((t: any) => t && typeof t === 'object')
          : [json];

        if (!tasks.length) return [];

        const dbTasks = Array.isArray((ap as any).tasks)
          ? (ap as any).tasks
          : parseMaybeJsonArray((ap as any).tasks);
        const dbTasksBySort = new Map<number, any>();
        dbTasks.forEach((task: any) => dbTasksBySort.set(task.task_sort_order, task));

        const parsedTemplate = parseMaybeJsonObject((ap as any).template);
        const templateTasks = extractTemplateTasks(parsedTemplate);

        const actionPlanTeamId = typeof ap.team_id === 'number' ? ap.team_id : undefined;

        return tasks.map((task: any, index: number) => {
          const taskIndex = typeof task.task_index === 'number' ? task.task_index : index;
          const dbTask = dbTasksBySort.get(taskIndex + 1);
          const templateTask = templateTasks[taskIndex] ?? {};

          return {
            ...templateTask,
            ...task,
            // Parent action plan metadata for detail view
            action_plan_id: ap.id,
            team_id: actionPlanTeamId,
            recommendation_name: ap.recommendation_name || templateTask.task || templateTask.title,
            template_description: parsedTemplate?.description,

            task_id: dbTask?.id ?? `${ap.id}_${taskIndex}`,
            recommendation_template_id: task.recommendation_template_id ?? ap.recommendation ?? undefined,
            task_index: taskIndex,
            task_title: task.task || task.task_title || task.title || templateTask.task || templateTask.title,
            task_description: task.description || task.task_description || templateTask.description,
            time_estimate: task.time || task.time_estimate || task.duration || templateTask.time || templateTask.time_estimate || templateTask.duration,
            title: task.task || task.title || task.task_title || templateTask.task || templateTask.title || 'Untitled',
            notes: dbTask?.notes ?? task.notes ?? ap.notes,
            // Prefer the first non-empty source; fall back to empty arrays only if no populated source exists.
            content: firstNonEmptyArray(task.content, templateTask.content, dbTask?.content),
            resources: firstNonEmptyArray(task.resources, templateTask.resources, dbTask?.resources),
          };
        });
      });

    // Keep local items that haven't been saved yet (not in server data)
    const savedIds = new Set(
      savedPlans
        .map((sp) => `${sp.recommendation_template_id ?? 'unknown'}_${sp.task_index ?? 0}`)
    );

    // If the server returned no action plans for this period, clear local-only items.
    let localOnlyItems: any[];
    if (plans.length === 0) {
      // authoritative empty set from server — drop everything
      localOnlyItems = [];
    } else {
      // Otherwise, drop local-only items if the period or team changed, else preserve unsaved local items
      localOnlyItems = (lastPeriodRef.current !== (assessmentPeriodId ?? null) || lastTeamRef.current !== (teamId ?? null))
        ? []
        : plan.filter(item => {
            const itemId = `${item.recommendation_template_id ?? 'unknown'}_${item.task_index ?? 0}`;
            return !savedIds.has(itemId);
          });
    }

    // Combine server data with local-only additions
    const combined = [...savedPlans, ...localOnlyItems];
    dispatch(resetPlanFromServer(combined));
    loadedIdsRef.current = currentIds;
    lastPeriodRef.current = assessmentPeriodId ?? null;
    lastTeamRef.current = teamId ?? null;
    forceSyncRef.current = false;
  }, [existingActionPlans, assessmentPeriodId, teamId, dispatch, deletedActionPlanIds, plan]);

  return {
    resetForRefresh: () => {
      loadedIdsRef.current = '';
      lastPeriodRef.current = null;
      lastTeamRef.current = null;
    },
  };
};
